import type { Node } from '@atlaskit/editor-prosemirror/model';
import {
	getMauiIdFromAdfNode,
	getMauiIdFromUrl,
} from '@atlassian/malleable-ui-components/editor-remix/remix-ids';
import type { RemixResultTracking } from '@atlassian/malleable-ui-components/types';

type MediaLocalIdSource = 'mediaSingle' | 'media';

type TrackedResult = Pick<
	RemixResultTracking,
	'attributes' | 'onChange' | 'previousLocalId' | 'previousResultId'
> & {
	appliedAt: number;
	currentResultId: string | undefined;
	localId?: string;
	mediaId?: string;
	mediaLocalIdSource?: MediaLocalIdSource;
	resultId: string;
};

type RemixResultTracker = {
	beginCwrReplacement: (replacement: CwrRemixReplacement) => void;
	cancelCwrReplacement: (replacement: CwrRemixReplacement, doc: Node) => void;
	clear: () => void;
	completeCwrReplacement: (replacement: CwrRemixReplacement, resultId: string, doc: Node) => void;
	track: (options: RemixResultTracking, doc: Node) => void;
	update: (doc: Node) => void;
};

type CwrRemixReplacement = {
	previousLocalId: string;
	previousResultId: string;
};

const cwrReplacementKey = (replacement: CwrRemixReplacement): string =>
	`${replacement.previousLocalId}:${replacement.previousResultId}`;

const getResultId = (node: Node): string | undefined => {
	if (node.type.name === 'media') {
		return typeof node.attrs.id === 'string' ? node.attrs.id : undefined;
	}
	if (node.type.name === 'mediaSingle') {
		return node.firstChild ? getResultId(node.firstChild) : undefined;
	}
	if (node.type.name === 'embedCard') {
		return getMauiIdFromUrl(node.attrs.url);
	}
	return getMauiIdFromAdfNode(node);
};

const findResultId = (doc: Node, localId: string): string | undefined => {
	let resultId: string | undefined;
	doc.descendants((node) => {
		if (node.attrs.localId === localId) {
			resultId = getResultId(node) ?? resultId;
		}
	});
	return resultId;
};

// Finds the target media and chooses a unique localId for future updates,
// preferring the mediaSingle wrapper over the media node.
export const findMedia = (
	doc: Node,
	mediaId: string,
): { found: boolean; localId?: string; localIdSource?: MediaLocalIdSource } => {
	let found = false;
	let mediaLocalId: string | undefined;
	let wrapperLocalId: string | undefined;
	const localIdCounts = new Map<string, number>();
	doc.descendants((node, _pos, parent) => {
		if (node.type.name !== 'media') {
			return;
		}
		const nodeLocalId = node.attrs.localId;
		const parentLocalId = parent?.type.name === 'mediaSingle' ? parent.attrs.localId : undefined;
		if (typeof nodeLocalId === 'string') {
			const key = `media:${nodeLocalId}`;
			localIdCounts.set(key, (localIdCounts.get(key) ?? 0) + 1);
		}
		if (typeof parentLocalId === 'string') {
			const key = `mediaSingle:${parentLocalId}`;
			localIdCounts.set(key, (localIdCounts.get(key) ?? 0) + 1);
		}
		if (node.attrs.id === mediaId) {
			found = true;
			mediaLocalId = typeof nodeLocalId === 'string' ? nodeLocalId : undefined;
			wrapperLocalId = typeof parentLocalId === 'string' ? parentLocalId : undefined;
		}
	});
	if (wrapperLocalId && localIdCounts.get(`mediaSingle:${wrapperLocalId}`) === 1) {
		return { found, localId: wrapperLocalId, localIdSource: 'mediaSingle' };
	}
	if (mediaLocalId && localIdCounts.get(`media:${mediaLocalId}`) === 1) {
		return { found, localId: mediaLocalId, localIdSource: 'media' };
	}
	return { found };
};

// Compares matching media in the previous and current documents so a surviving copy
// is not mistaken for a replacement of the tracked result.
const findMediaIdByLocalId = (
	doc: Node,
	oldDoc: Node,
	localId: string,
	localIdSource: MediaLocalIdSource,
	preferredMediaId: string,
): string | undefined => {
	const matchingIds = (docToSearch: Node): string[] => {
		const ids: string[] = [];
		docToSearch.descendants((node, _pos, parent) => {
			if (
				node.type.name === 'media' &&
				(localIdSource === 'mediaSingle'
					? parent?.type.name === 'mediaSingle' && parent.attrs.localId === localId
					: node.attrs.localId === localId) &&
				typeof node.attrs.id === 'string'
			) {
				ids.push(node.attrs.id);
			}
		});
		return ids;
	};
	const currentIds = matchingIds(doc);
	if (currentIds.includes(preferredMediaId)) {
		return preferredMediaId;
	}
	const previousIds = matchingIds(oldDoc);
	const newId = currentIds.filter((id) => !previousIds.includes(id)).at(-1);
	if (newId) {
		return newId;
	}
	// A surviving copy is not a replacement for the tracked image.
	return previousIds.length > 1 ? undefined : currentIds.at(-1);
};

// Resolves the result currently represented by the tracked node.
// Media without a unique localId falls back to its original media ID.
const getCurrentResultId = (result: TrackedResult, doc: Node, oldDoc: Node): string | undefined => {
	if (!result.mediaId) {
		return result.localId ? findResultId(doc, result.localId) : undefined;
	}
	if (result.localId && result.mediaLocalIdSource) {
		return findMediaIdByLocalId(
			doc,
			oldDoc,
			result.localId,
			result.mediaLocalIdSource,
			result.resultId,
		);
	}
	return findMedia(doc, result.mediaId).found ? result.mediaId : undefined;
};

const containsResultId = (doc: Node, resultId: string): boolean => {
	let found = false;
	doc.descendants((node) => {
		if (getResultId(node) === resultId) {
			found = true;
			return false;
		}
		return !found;
	});
	return found;
};

export const createRemixResultTracker = (): RemixResultTracker => {
	const tracked = new Map<string, TrackedResult>();
	// CWR streams a replacement over several document updates before its final result is known.
	const pendingCwrReplacements = new Set<string>();
	let previousDoc: Node | undefined;
	const emit = (result: TrackedResult, action: Parameters<RemixResultTracking['onChange']>[0]) => {
		result.onChange(action, {
			...result.attributes,
			...(result.localId ? { localId: result.localId } : {}),
			resultId: result.resultId,
			elapsedMs: Date.now() - result.appliedAt,
		});
	};
	const isPendingCwrReplacement = (result: TrackedResult): boolean => {
		if (!result.localId) {
			return false;
		}
		return pendingCwrReplacements.has(
			cwrReplacementKey({ previousLocalId: result.localId, previousResultId: result.resultId }),
		);
	};
	const reconcilePendingCwrReplacement = (
		replacement: CwrRemixReplacement,
		doc: Node,
		resultId?: string,
	) => {
		pendingCwrReplacements.delete(cwrReplacementKey(replacement));
		for (const [key, result] of tracked) {
			if (
				result.localId !== replacement.previousLocalId ||
				result.resultId !== replacement.previousResultId
			) {
				continue;
			}

			const currentResultId = getCurrentResultId(result, doc, previousDoc ?? doc);
			if (currentResultId === result.resultId) {
				continue;
			}
			if (resultId && containsResultId(doc, resultId)) {
				emit(result, 'replaced');
				tracked.delete(key);
				continue;
			}

			result.currentResultId = currentResultId;
			if (!currentResultId) {
				emit(result, 'deleted');
			} else {
				emit(result, 'replaced');
			}
		}
	};
	const update = (doc: Node) => {
		const oldDoc = previousDoc;
		previousDoc = doc;
		if (tracked.size === 0 || !oldDoc || oldDoc === doc) {
			return;
		}
		const start = oldDoc.content.findDiffStart(doc.content);
		const end = oldDoc.content.findDiffEnd(doc.content);
		if (start === null || !end) {
			return;
		}
		const overlap = Math.max(0, start - Math.min(end.a, end.b));
		const changedLocalIds = new Set<string>();
		const changedMediaIds = new Set<string>();
		const collectChanged = (node: Node) => {
			if (typeof node.attrs.localId === 'string') {
				changedLocalIds.add(node.attrs.localId);
			}
			if (node.type.name === 'media' && typeof node.attrs.id === 'string') {
				changedMediaIds.add(node.attrs.id);
			}
		};
		oldDoc.nodesBetween(start, end.a + overlap, collectChanged);
		doc.nodesBetween(start, end.b + overlap, collectChanged);
		const results = new Map<string, string | undefined>();
		tracked.forEach((result) => {
			if (isPendingCwrReplacement(result)) {
				return;
			}
			if (
				!(result.localId && changedLocalIds.has(result.localId)) &&
				!(result.previousLocalId && changedLocalIds.has(result.previousLocalId)) &&
				!(result.mediaId && changedMediaIds.has(result.mediaId))
			) {
				return;
			}
			// Media lookups depend on the tracked result, so they are cached per result.
			const lookupKey = result.mediaId
				? `media:${result.mediaLocalIdSource ?? 'id'}:${result.localId ?? result.mediaId}:${result.resultId}`
				: `maui:${result.localId}`;
			if (!results.has(lookupKey)) {
				results.set(lookupKey, getCurrentResultId(result, doc, oldDoc));
			}
			const currentResultId = results.get(lookupKey);
			if (currentResultId === result.currentResultId) {
				return;
			}
			const wasPresent = result.currentResultId === result.resultId;
			result.currentResultId = currentResultId;
			if (currentResultId === result.resultId) {
				emit(result, 'restored');
			} else if (wasPresent) {
				const previousResultId = result.previousResultId;
				const revertedToPreviousResult =
					previousResultId !== undefined &&
					(currentResultId === previousResultId ||
						(currentResultId === undefined &&
							typeof result.previousLocalId === 'string' &&
							findResultId(doc, result.previousLocalId) === previousResultId));
				if (revertedToPreviousResult) {
					emit(result, 'reverted');
				} else if (!currentResultId) {
					emit(result, 'deleted');
				} else {
					emit(result, 'replaced');
				}
			}
		});
	};

	return {
		beginCwrReplacement(replacement) {
			pendingCwrReplacements.add(cwrReplacementKey(replacement));
		},
		cancelCwrReplacement(replacement, doc) {
			reconcilePendingCwrReplacement(replacement, doc);
		},
		track(options: RemixResultTracking, doc: Node) {
			if (previousDoc !== doc) {
				update(doc);
			}
			const media = options.mediaId ? findMedia(doc, options.mediaId) : undefined;
			const localId = options.mediaId ? media?.localId : options.localId;
			const resultId = options.mediaId
				? media?.found
					? options.mediaId
					: undefined
				: localId
					? findResultId(doc, localId)
					: undefined;
			if (!resultId || (options.resultId && options.resultId !== resultId)) {
				return;
			}
			const key = JSON.stringify([
				options.mediaId ? 'media' : 'maui',
				localId ?? options.mediaId,
				resultId,
				options.attributes.singleInstrumentationID,
			]);
			if (tracked.has(key)) {
				return;
			}
			const result: TrackedResult = {
				...options,
				localId,
				mediaLocalIdSource: media?.localIdSource,
				resultId,
				currentResultId: resultId,
				appliedAt: Date.now(),
			};
			tracked.set(key, result);
			emit(result, 'applied');
		},
		completeCwrReplacement(replacement, resultId, doc) {
			reconcilePendingCwrReplacement(replacement, doc, resultId);
		},
		update,
		clear() {
			tracked.clear();
			pendingCwrReplacements.clear();
			previousDoc = undefined;
		},
	};
};
